import React, { useState } from 'react';
import { Card, Spinner, Button, Modal, Form } from 'react-bootstrap';
import './AISuggestionsPanel.css';
import { getEventSearchSuggestions } from '../../helper';

const AISuggestionsPanel = ({ vendor, weekData }) => {
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [showPromptModal, setShowPromptModal] = useState(false);

  const [formValues, setFormValues] = useState({
    workType: '',
    eventFocus: '',
    pricingPreference: '',
    eventScale: '',
  });

  const handleOpenPrompt = () => setShowPromptModal(true);
  const handleClosePrompt = () => setShowPromptModal(false);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormValues(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitAndFetch = async () => {
    if (!vendor || !weekData.startDate || !weekData.endDate) return;

    setLoading(true);
    setSuggestions(null);
    setShowPromptModal(false);

    try {
      const truckDetails = {
        cuisines: vendor?.cuisine_tags || ['Unknown'],
        baseLocation: vendor?.hometown || 'Melbourne',
        travelRadiusKm: vendor?.travel_radius_km || 100,
        avgMealPrice: vendor?.average_meal_price || 18,
        dailySaleTarget: vendor?.target_daily_sales || 2500,
        maxEventsPerWeek: 4,
        eventTypePriority: formValues.eventFocus || vendor?.event_type_preference || 'Public',
        allowCatering: vendor?.catering_allowed ?? true,
        indoorPreferred: vendor?.indoor_preference ?? false,
        multiDayOk: vendor?.multi_day_ok ?? true,
        userPreferences: formValues,
      };

      const aiResult = await getEventSearchSuggestions({
        weekNumber: weekData.week,
        startDate: weekData.startDate,
        endDate: weekData.endDate,
        truckDetails,
      });

      setSuggestions({ success: true, content: aiResult });
    } catch (err) {
      console.error('❌ Failed to fetch AI suggestions:', err);
      setSuggestions(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button variant="info" className="mb-3" onClick={handleOpenPrompt} disabled={loading}>
        {loading ? 'Loading Suggestions…' : 'Ask WTT AI for Event Suggestions'}
      </Button>

      {loading && <Spinner animation="border" variant="primary" className="ml-3" />}

      <Modal show={showPromptModal} onHide={handleClosePrompt}>
        <Modal.Header closeButton>
          <Modal.Title>Help us tailor your event suggestions</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="workType">
              <Form.Label>What do you like to work at?</Form.Label>
              <Form.Control
                type="text"
                name="workType"
                value={formValues.workType}
                placeholder="e.g. Late-night crowd, family markets"
                onChange={handleFormChange}
              />
            </Form.Group>

            <Form.Group controlId="eventFocus">
              <Form.Label>What kind of events do you focus on?</Form.Label>
              <Form.Control
                as="select"
                name="eventFocus"
                value={formValues.eventFocus}
                onChange={handleFormChange}
              >
                <option value="">Select one</option>
                <option value="Public">Public</option>
                <option value="Christmas Carols">Christmas Carols</option>
                <option value="Market Stalls">Market Stalls</option>
                <option value="Music Concerts">Music Concerts</option>
                <option value="Community Fairs">Community Fairs</option>
                <option value="Cultural Events">Cultural Events</option>
                <option value="All of the Above">All of the Above</option>
              </Form.Control>
            </Form.Group>

            <Form.Group controlId="pricingPreference">
              <Form.Label>What pricing style suits you?</Form.Label>
              <Form.Control
                as="select"
                name="pricingPreference"
                value={formValues.pricingPreference}
                onChange={handleFormChange}
              >
                <option value="">Select one</option>
                <option value="Low fee / Low revenue">Low fee / Low revenue</option>
                <option value="High fee / High revenue">High fee / High revenue</option>
                <option value="Balanced">Balanced</option>
                
              </Form.Control>
            </Form.Group>

            <Form.Group controlId="eventScale">
              <Form.Label>Event scale preference?</Form.Label>
              <Form.Control
                as="select"
                name="eventScale"
                value={formValues.eventScale}
                onChange={handleFormChange}
              >
                <option value="">Select one</option>
                <option value="Major events only">Major events only</option>
                <option value="Minor events okay">Minor events okay</option>
                <option value="Let WTT decide">Let WTT decide</option>
                <option value="All of the above">All of the above</option>
              </Form.Control>
            </Form.Group>

            <Button variant="primary" onClick={handleSubmitAndFetch} disabled={loading}>
              Submit & Ask WTT AI to help you
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      {suggestions?.success && (
        <Card className="ai-suggestion-card shadow-sm mt-3">
          <Card.Body>
            <Card.Title className="text-info font-weight-bold">
              WTT AI Event Strategy — Week {weekData.week}
            </Card.Title>
            <Card.Text style={{ whiteSpace: 'pre-wrap' }}>
              {suggestions.content}
            </Card.Text>
          </Card.Body>
        </Card>
      )}
    </>
  );
};

export default AISuggestionsPanel;
